import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { setIndexedDb, getIndexedDb } from '../utils/indexedDbStorage';

/**
 * Purga de forma preventiva claves secundarias de localStorage para liberar espacio
 * en caso de QuotaExceededError, preservando siempre credenciales y sesión activa.
 */
function tryEvictOldStorage(protectKey: string) {
    if (typeof window === 'undefined') return;
    try {
        const keysToEvict: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
                k.includes('msal') ||
                k.includes('token') ||
                k.includes('user') ||
                k.includes('login') ||
                k.includes('auth') ||
                k.includes('project_selection') ||
                k === protectKey
            ) {
                continue;
            }
            if (k.startsWith('klyd_offline_') && !k.endsWith(protectKey)) {
                keysToEvict.push(k);
            }
        }
        keysToEvict.slice(0, 5).forEach(k => {
            try {
                localStorage.removeItem(k);
                console.log(`[Storage Eviction] Clave liberada de localStorage: ${k}`);
            } catch (_) {}
        });
    } catch (e) {
        console.warn('[Storage Eviction] Error durante purga preventiva:', e);
    }
}

/**
 * Escribe en localStorage de manera defensiva. Atrapa QuotaExceededError
 * para evitar que excepciones de almacenamiento boten la interfaz de usuario.
 */
export const safeSetLocalStorage = (key: string, value: string): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (err: any) {
        const isQuota = err && (
            err.name === 'QuotaExceededError' ||
            err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            err.code === 22 ||
            err.code === 1014 ||
            (err.message && err.message.toLowerCase().includes('quota'))
        );
        if (isQuota) {
            console.warn(`[useSyncState] Cuota de localStorage excedida al guardar '${key}'. Intentando purga preventiva...`);
            tryEvictOldStorage(key);
            try {
                localStorage.setItem(key, value);
                return true;
            } catch (retryErr) {
                console.warn(`[useSyncState] Persistencia en localStorage omitida para '${key}' debido al límite de 5MB. Se preserva en IndexedDB y servidor.`);
                return false;
            }
        }
        console.error(`[useSyncState] Error al escribir en localStorage '${key}':`, err);
        return false;
    }
};

const safeStringify = (obj: any): string => {
    try {
        return JSON.stringify(obj);
    } catch (e) {
        console.error("[useSyncState] JSON.stringify failed. Payload might be too large or contain circular references.", e);
        return "";
    }
};

interface SyncQueueItem {
    collectionName: string;
    docId: string;
    payload: any;
    timestamp: string;
}

// Helper to check if error indicates a network/connectivity issue
const isNetworkError = (error: any): boolean => {
    if (!error) return false;
    const msg = (error.message || "").toLowerCase();
    return (
        msg.includes("fetch") || 
        msg.includes("network") || 
        msg.includes("timeout") || 
        msg.includes("failed to fetch") || 
        error.status === 0 || 
        error.code === "PGRST301" || 
        error.code === "TypeError"
    );
};

// Queue handling: Enqueue item for offline syncing
const enqueueOfflineSync = (collectionName: string, docId: string, payload: any) => {
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        let queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
        // Remove any existing entry for the same doc
        queue = queue.filter(item => !(item.collectionName === collectionName && item.docId === docId));
        // Add new entry
        queue.push({
            collectionName,
            docId,
            payload,
            timestamp: new Date().toISOString()
        });
        safeSetLocalStorage("klyd_offline_sync_queue", JSON.stringify(queue));
        console.log(`[Offline Queue] Enqueued sync for ${collectionName}/${docId}. Queue size: ${queue.length}`);
    } catch (e) {
        console.error("[Offline Queue] Error enqueueing:", e);
    }
};

// Queue handling: Dequeue item once successfully synced
const dequeueOfflineSync = (collectionName: string, docId: string) => {
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        if (!queueStr) return;
        let queue: SyncQueueItem[] = JSON.parse(queueStr);
        const originalLength = queue.length;
        queue = queue.filter(item => !(item.collectionName === collectionName && item.docId === docId));
        if (queue.length !== originalLength) {
            safeSetLocalStorage("klyd_offline_sync_queue", JSON.stringify(queue));
            console.log(`[Offline Queue] Dequeued sync for ${collectionName}/${docId}. Queue size: ${queue.length}`);
        }
    } catch (e) {
        console.error("[Offline Queue] Error dequeueing:", e);
    }
};

// Registro de funciones de combinación específicas por documento (ej. 'weeklyPlans', 'globalProfiles').
type Merger = (local: any, remote: any) => any;
const docMergers = new Map<string, Merger>();
export const registerDocMerger = (docId: string, merger: Merger) => docMergers.set(docId, merger);

const extractList = (payload: any) => (payload && payload.list !== undefined ? payload.list : payload);

/**
 * Extrae una marca temporal numérica de un objeto o documento.
 * Busca campos estándar como updatedAt, statusUpdatedAt, timestamp, etc.
 */
const extractTimestamp = (obj: any): number => {
    if (!obj || typeof obj !== 'object') return 0;
    const candidates = [
        obj.updatedAt,
        obj.statusUpdatedAt,
        obj.updated_at,
        obj.timestamp,
        obj.createdAt,
        obj.created_at,
        obj.fecha,
        obj.date
    ];
    for (const c of candidates) {
        if (typeof c === 'string' || typeof c === 'number') {
            const parsed = typeof c === 'number' ? c : Date.parse(c);
            if (!isNaN(parsed) && parsed > 0) return parsed;
        }
    }
    return 0;
};

/**
 * Fusión cronológica universal para colecciones o listas de elementos identificados (por id, code, taskId, etc.).
 * Si un elemento fue modificado más recientemente en el servidor o localmente, prevalece la versión más reciente.
 * Nuevos elementos en local o remoto se conservan sin destruirse.
 */
const mergeEntityListsChronological = (localList: any[], remoteList: any[]): any[] => {
    if (!Array.isArray(remoteList) || remoteList.length === 0) return Array.isArray(localList) ? localList : [];
    if (!Array.isArray(localList) || localList.length === 0) return remoteList;

    // Detectar clave primaria común
    const sample = localList.find(x => x && typeof x === 'object') || remoteList.find(x => x && typeof x === 'object');
    if (!sample) return localList;

    let keyProp = 'id';
    if (sample.id !== undefined) keyProp = 'id';
    else if (sample.code !== undefined) keyProp = 'code';
    else if (sample.uuid !== undefined) keyProp = 'uuid';
    else if (sample.taskId !== undefined && sample.unitId !== undefined) keyProp = '__task_unit__';
    else {
        // Sin clave obvia, fallback a local
        return localList;
    }

    const getKey = (item: any): string => {
        if (!item || typeof item !== 'object') return '';
        if (keyProp === '__task_unit__') return `${item.taskId}_${item.unitId}`;
        return String(item[keyProp] ?? '');
    };

    const localMap = new Map<string, any>();
    localList.forEach((item, idx) => {
        const k = getKey(item) || `idx_${idx}`;
        localMap.set(k, item);
    });

    const mergedMap = new Map<string, any>();

    // 1. Procesar elementos remotos y resolver conflictos contra local
    remoteList.forEach((remoteItem, idx) => {
        const k = getKey(remoteItem) || `idx_${idx}`;
        const localItem = localMap.get(k);

        if (!localItem) {
            // El servidor tiene un elemento que el cliente local no tiene (ej. creado por otro usuario)
            mergedMap.set(k, remoteItem);
            return;
        }

        // REGLA DE MADUREZ UNIVERSAL:
        // Si ambos objetos tienen un campo 'status' o 'estado', el estado más avanzado prevalece
        // sobre un estado primitivo o inicial.
        const STATUS_MATURITY_RANKS: Record<string, number> = {
            // Planificación
            'En Planificación': 1, 'En Ejecución': 2, 'En Revisión': 3, 'Cerrada': 4,
            // Calidad / Protocolos
            'Inactivo': 1, 'Abierto': 2, 'Rechazado': 2, 'En Aprobación': 3, 'Resuelto': 4, 'Cerrado': 4,
            // Estados de Pago / Contratos
            'Borrador': 1, 'Generado': 2, 'Aprobado': 3, 'Pagado': 4,
            // Tareas y Actividades
            'Pendiente': 1, 'No Iniciada': 1, 'En Proceso': 2, 'Completada': 3, 'Finalizada': 4
        };

        const getRank = (item: any): number => {
            const s = item?.status || item?.estado;
            return (s && STATUS_MATURITY_RANKS[s]) ? STATUS_MATURITY_RANKS[s] : 0;
        };

        const localRank = getRank(localItem);
        const remoteRank = getRank(remoteItem);

        const localTs = extractTimestamp(localItem);
        const remoteTs = extractTimestamp(remoteItem);

        // REGLA UNIVERSAL CRONOLÓGICA (Regla 11 AGENTS.md):
        // Si hay una edición explícita con timestamp más reciente en un extremo, esa prevalece.
        // La madurez (rank) opera como criterio de desempate cuando los timestamps son iguales o inexistentes.
        if (localTs > remoteTs) {
            mergedMap.set(k, { ...remoteItem, ...localItem });
        } else if (remoteTs > localTs) {
            mergedMap.set(k, { ...localItem, ...remoteItem });
        } else if (remoteRank > localRank) {
            // Mismo timestamp: desempate por madurez operativa
            mergedMap.set(k, { ...localItem, ...remoteItem });
        } else if (localRank > remoteRank) {
            mergedMap.set(k, { ...remoteItem, ...localItem });
        } else {
            mergedMap.set(k, { ...remoteItem, ...localItem });
        }
    });

    // 2. Conservar elementos creados localmente que aún no están en el servidor
    localList.forEach((localItem, idx) => {
        const k = getKey(localItem) || `idx_${idx}`;
        if (!mergedMap.has(k)) {
            mergedMap.set(k, localItem);
        }
    });

    return Array.from(mergedMap.values());
};

/**
 * Fusión cronológica universal para diccionarios y objetos clave-valor (ej. protocolConfigs, savedProtocols, matrixData).
 */
const mergeObjectsChronological = (localObject: Record<string, any>, remoteObject: Record<string, any>): Record<string, any> => {
    if (!remoteObject || typeof remoteObject !== 'object' || Array.isArray(remoteObject)) return localObject;
    if (!localObject || typeof localObject !== 'object' || Array.isArray(localObject)) return remoteObject;

    const merged: Record<string, any> = { ...remoteObject };

    for (const key of Object.keys(localObject)) {
        const localVal = localObject[key];
        const remoteVal = remoteObject[key];

        if (remoteVal === undefined) {
            merged[key] = localVal;
            continue;
        }

        // Si ambos son objetos, evaluar marcas temporales
        if (localVal && remoteVal && typeof localVal === 'object' && typeof remoteVal === 'object' && !Array.isArray(localVal) && !Array.isArray(remoteVal)) {
            const localTs = extractTimestamp(localVal);
            const remoteTs = extractTimestamp(remoteVal);
            if (localTs > remoteTs) {
                merged[key] = { ...remoteVal, ...localVal };
            } else if (remoteTs > localTs) {
                merged[key] = { ...localVal, ...remoteVal };
            } else {
                merged[key] = { ...remoteVal, ...localVal };
            }
        } else {
            // Para valores primitivos en diccionarios (ej. matrixData celdas), el cambio local activo prevalece
            merged[key] = localVal;
        }
    }

    return merged;
};

/**
 * Fusión universal por orden cronológico aplicada a TODOS los módulos y documentos.
 */
export const mergeUniversalChronological = (local: any, remote: any): any => {
    if (remote === null || remote === undefined) return local;
    if (local === null || local === undefined) return remote;

    // Caso 1: Ambos son Arrays (listas de entidades: tareas, unidades, asignaciones, etc.)
    if (Array.isArray(local) && Array.isArray(remote)) {
        return mergeEntityListsChronological(local, remote);
    }

    // Caso 2: Ambos son Objetos (diccionarios, configuraciones, metadatos)
    // Regla de Oro: nunca se reemplaza un documento completo por su marca raíz; siempre se fusiona elemento por elemento.
    // Aplica a todos los documentos de tipo objeto (savedProtocols, protocolConfigs, matrixData, etc.).
    if (typeof local === 'object' && typeof remote === 'object' && !Array.isArray(local) && !Array.isArray(remote)) {
        return mergeObjectsChronological(local, remote);
    }

    // Caso 3: Primitivos
    return local;
};

const mergeWithRemote = async (collectionName: string, docId: string, payload: any): Promise<any> => {
    try {
        const { data, error } = await supabase
            .from('app_data')
            .select('payload')
            .eq('collection_name', collectionName)
            .eq('doc_id', docId)
            .maybeSingle();

        if (error || !data || data.payload == null) return payload;

        const isListPayload = payload && payload.list !== undefined;
        const localData = extractList(payload);
        const remoteData = extractList(data.payload);

        // 1. Si existe un merger especializado para este docId (ej. weeklyPlans, globalProfiles), usarlo
        const customMerger = docMergers.get(docId);
        let merged: any;
        if (customMerger) {
            merged = customMerger(localData, remoteData);
        } else {
            // 2. Si no, aplicar el merger cronológico universal que protege todos los demás módulos
            merged = mergeUniversalChronological(localData, remoteData);
        }

        return isListPayload ? { ...payload, list: merged } : merged;
    } catch (e) {
        console.warn(`[Supabase Sync] Merge with remote failed for ${docId}, saving local payload.`, e);
        return payload;
    }
};

let isSyncingQueue = false;

// Attempt to sync all pending offline queue items
const syncOfflineQueue = async () => {
    if (isSyncingQueue) return;
    try {
        const queueStr = localStorage.getItem("klyd_offline_sync_queue");
        if (!queueStr) return;
        const queue: SyncQueueItem[] = JSON.parse(queueStr);
        if (queue.length === 0) return;

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            console.log("[Offline Queue] WebView reports offline status. Retrying sync skipped.");
            return;
        }

        isSyncingQueue = true;
        console.log(`[Offline Queue] Triggered retry for ${queue.length} pending items...`);

        for (const item of queue) {
            console.log(`[Offline Queue] Retrying upload for ${item.collectionName}/${item.docId}...`);
            const mergedQueuePayload = await mergeWithRemote(item.collectionName, item.docId, item.payload);
            const { error } = await supabase
                .from('app_data')
                .upsert({
                    collection_name: item.collectionName,
                    doc_id: item.docId,
                    payload: mergedQueuePayload,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'collection_name,doc_id' });

            if (error) {
                console.error(`[Offline Queue] Failed to sync ${item.collectionName}/${item.docId}:`, error);
                if (!isNetworkError(error)) {
                    // Non-network errors are removed to prevent infinite blocking loops
                    dequeueOfflineSync(item.collectionName, item.docId);
                }
                break; // Stop execution of next items if it was a network failure
            } else {
                console.log(`[Offline Queue] Successfully uploaded ${item.collectionName}/${item.docId}`);
                dequeueOfflineSync(item.collectionName, item.docId);
            }
        }
    } catch (e) {
        console.error("[Offline Queue] Exception during queue retry worker:", e);
    } finally {
        isSyncingQueue = false;
    }
};

// Set up global browser/Capacitor synchronization listeners
if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
        console.log("[Network] Connectivity restored. Flushing offline queue...");
        syncOfflineQueue();
    });

    // Flushing offline queue also on manual pull-to-refresh
    window.addEventListener("klyd_sync_refresh", () => {
        console.log("[Network] Manual refresh event. Flushing offline queue...");
        syncOfflineQueue();
    });

    // Run interval check every 30 seconds for passive background recovery
    setInterval(() => {
        syncOfflineQueue();
    }, 30000);
}

/**
 * useSyncState (Supabase & Offline-First local storage)
 * Syncs a local state variable with Supabase using Realtime, falling back locally to prevent offline data loss.
 */
function getCachedOrInitial<T>(offlineKey: string, initialValue: T): T {
    try {
        if (typeof window !== "undefined") {
            const cached = localStorage.getItem(offlineKey);
            if (cached !== null) {
                const parsed = JSON.parse(cached);
                
                if (Array.isArray(initialValue)) {
                    const extracted = parsed.list !== undefined ? parsed.list : parsed;
                    return (Array.isArray(extracted) ? extracted : initialValue) as unknown as T;
                } else if (typeof initialValue === 'object' && initialValue !== null) {
                    return ((typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) ? parsed : initialValue) as unknown as T;
                } else {
                    return parsed as T;
                }
            }
        }
    } catch (e) {
        console.error(`[Offline Cache] Error loading initial fallback:`, e);
    }
    return initialValue;
}

export function useSyncState<T>(collectionName: string, docId: string, initialValue: T) {
    const offlineKey = `klyd_offline_${collectionName}_${docId}`;

    // 1. Initial State Initialization: Sychronously read local cached value first (Instant Offline Render)
    const [state, setState] = useState<T>(() => {
        return getCachedOrInitial(offlineKey, initialValue);
    });

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadedFromServer, setLoadedFromServer] = useState(false);
    const isRemoteUpdate = useRef(false);
    const isMounted = useRef(false);
    const lastSavedState = useRef<string>(safeStringify(state));
    const stateRef = useRef(state);

    const lastCollection = useRef(collectionName);
    const lastDoc = useRef(docId);

    // Reset state and loaded status synchronously during render if collection or document ID changes
    if (lastCollection.current !== collectionName || lastDoc.current !== docId) {
        lastCollection.current = collectionName;
        lastDoc.current = docId;
        const resetVal = getCachedOrInitial(offlineKey, initialValue);
        setState(resetVal);
        setHasLoaded(false);
        setLoadedFromServer(false);
        stateRef.current = resetVal;
        lastSavedState.current = safeStringify(resetVal);
    }

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // 2. Fetch initial data and configure Supabase Realtime channel
    useEffect(() => {
        console.log(`[Supabase Sync] Switch to ${collectionName}/${docId}`);
        isMounted.current = true;
        setHasLoaded(false);
        setLoadedFromServer(false);
        isRemoteUpdate.current = false;

        // Try reading latest local cache again in case parameters changed
        try {
            const cached = localStorage.getItem(offlineKey);
            if (cached !== null) {
                const parsed = JSON.parse(cached);
                let cachedValue: any;
                if (Array.isArray(initialValue)) {
                    const extracted = parsed.list !== undefined ? parsed.list : parsed;
                    cachedValue = Array.isArray(extracted) ? extracted : initialValue;
                } else if (typeof initialValue === 'object' && initialValue !== null) {
                    cachedValue = (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) ? parsed : initialValue;
                } else {
                    cachedValue = parsed;
                }
                setState(cachedValue);
                lastSavedState.current = safeStringify(cachedValue);
            } else {
                setState(initialValue);
                lastSavedState.current = safeStringify(initialValue);
            }
        } catch (e) {
            console.error(`[Offline Cache] Error reading cache for ${docId}:`, e);
        }

        // Complementar con lectura asíncrona de IndexedDB (por si localStorage estaba vacío o limitado por cuota)
        getIndexedDb(offlineKey).then((idbVal) => {
            if (!isMounted.current || idbVal === null || idbVal === undefined) return;
            let cachedValue: any;
            if (Array.isArray(initialValue)) {
                const extracted = (idbVal as any).list !== undefined ? (idbVal as any).list : idbVal;
                cachedValue = Array.isArray(extracted) ? extracted : initialValue;
            } else if (typeof initialValue === 'object' && initialValue !== null) {
                cachedValue = (typeof idbVal === 'object' && idbVal !== null && !Array.isArray(idbVal)) ? idbVal : initialValue;
            } else {
                cachedValue = idbVal;
            }
            const currentStr = safeStringify(stateRef.current);
            const initialStr = safeStringify(initialValue);
            if (currentStr === initialStr && safeStringify(cachedValue) !== initialStr) {
                setState(cachedValue);
                lastSavedState.current = safeStringify(cachedValue);
            }
        }).catch((err) => {
            console.warn(`[useSyncState] Error leyendo IndexedDB para ${docId}:`, err);
        });

        const fetchInitialData = async () => {
            try {
                // Check if this document has pending offline changes that are not synced yet
                const queueStr = localStorage.getItem("klyd_offline_sync_queue");
                const queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
                const hasPendingSync = queue.some(item => item.collectionName === collectionName && item.docId === docId);

                // If offline changes are pending, skip initial fetch to prevent overwriting user modifications
                if (hasPendingSync) {
                    console.log(`[Supabase Sync] Local offline modifications are pending for ${docId}. Skipping remote fetch to preserve work.`);
                    setHasLoaded(true);
                    return;
                }

                const { data, error } = await supabase
                    .from('app_data')
                    .select('payload')
                    .eq('collection_name', collectionName)
                    .eq('doc_id', docId)
                    .limit(1)
                    .maybeSingle();

                if (!isMounted.current) return;

                if (error) {
                    console.error(`[Supabase Sync] Fetch error for ${docId}:`, error);
                    // Do NOT set hasLoaded(true) on network errors, otherwise components might think
                    // the database is completely empty and trigger default seed over-writes.
                    return;
                }

                setLoadedFromServer(true);

                if (data && data.payload !== null && data.payload !== undefined) {
                    const fetchedData = data.payload;
                    let newValue: any;

                    if (Array.isArray(initialValue)) {
                        const extracted = fetchedData.list !== undefined ? fetchedData.list : fetchedData;
                        newValue = Array.isArray(extracted) ? extracted : initialValue;
                    } else if (typeof initialValue === 'object' && initialValue !== null) {
                        newValue = (typeof fetchedData === 'object' && fetchedData !== null && !Array.isArray(fetchedData)) ? fetchedData : initialValue;
                    } else {
                        newValue = fetchedData;
                    }

                    if (newValue !== undefined) {
                        const isRemoteEmpty = Array.isArray(newValue) ? newValue.length === 0 : Object.keys(newValue).length === 0;
                        const isLocalModified = safeStringify(stateRef.current) !== safeStringify(initialValue);

                        if (isRemoteEmpty && isLocalModified) {
                            console.warn(`[Supabase Sync] Server returned empty for ${docId}, but local state is modified. Preserving local.`);
                        } else {
                            console.log(`[Supabase Sync] Initial remote data loaded for ${docId}`);
                            
                            // Si el estado local tiene datos (desde cache offline), fusionar cronológicamente
                            // para que no se pierdan cambios locales pendientes ni se reviertan estados más recientes
                            let finalInitialValue = newValue;
                            const currentLocal = stateRef.current;
                            const hasLocalData = currentLocal && safeStringify(currentLocal) !== safeStringify(initialValue);
                            if (hasLocalData) {
                                try {
                                    const customMerger = docMergers.get(docId);
                                    const localData = extractList(currentLocal);
                                    const remoteData = extractList(newValue);
                                    const merged = customMerger
                                        ? customMerger(localData, remoteData)
                                        : mergeUniversalChronological(localData, remoteData);
                                    
                                    const currentAny = currentLocal as any;
                                    finalInitialValue = (Array.isArray(initialValue) || (currentAny && currentAny.list !== undefined))
                                        ? (currentAny && currentAny.list !== undefined ? { ...currentAny, list: merged } : merged)
                                        : merged;
                                } catch (mergeErr) {
                                    console.warn(`[Supabase Sync] Error merging initial cache with remote for ${docId}:`, mergeErr);
                                    finalInitialValue = newValue;
                                }
                            }

                            isRemoteUpdate.current = true;
                            setState(finalInitialValue);
                            lastSavedState.current = safeStringify(finalInitialValue);
                            setIndexedDb(offlineKey, finalInitialValue);
                            safeSetLocalStorage(offlineKey, safeStringify(finalInitialValue));
                        }
                    }
                } else {
                    console.log(`[Supabase Sync] Document ${collectionName}/${docId} does not exist yet.`);
                }
            } catch (err) {
                console.error(`[Supabase Sync] Exception during fetch Initial Data for ${docId}:`, err);
            } finally {
                setHasLoaded(true);
            }
        };

        fetchInitialData();

        // Subscribe to Supabase Realtime changes
        const channel = supabase.channel(`public:app_data:${collectionName}:${docId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'app_data',
                    filter: `collection_name=eq.${collectionName}`
                },
                (payload) => {
                    const newRecord = payload.new as any;
                    if (!newRecord || newRecord.doc_id !== docId || newRecord.collection_name !== collectionName) return;

                    // If we have pending offline sync for this, ignore realtime updates to prevent conflicts
                    const queueStr = localStorage.getItem("klyd_offline_sync_queue");
                    const queue: SyncQueueItem[] = queueStr ? JSON.parse(queueStr) : [];
                    if (queue.some(item => item.collectionName === collectionName && item.docId === docId)) {
                        console.log(`[Supabase Sync] Ignoring realtime update for ${docId} due to pending local offline modifications.`);
                        return;
                    }

                    const fetchedData = newRecord.payload;
                    let newValue: any;

                    if (Array.isArray(initialValue)) {
                        const extracted = fetchedData.list !== undefined ? fetchedData.list : fetchedData;
                        newValue = Array.isArray(extracted) ? extracted : initialValue;
                    } else if (typeof initialValue === 'object' && initialValue !== null) {
                        newValue = (typeof fetchedData === 'object' && fetchedData !== null && !Array.isArray(fetchedData)) ? fetchedData : initialValue;
                    } else {
                        newValue = fetchedData;
                    }

                    if (newValue !== undefined) {
                        // Protect unsaved local modifications: merge chronologically instead of dropping
                        const currentLocalStr = safeStringify(stateRef.current);
                        const hasUnsavedLocal = currentLocalStr !== "" && currentLocalStr !== lastSavedState.current;

                        if (hasUnsavedLocal) {
                            try {
                                const customMerger = docMergers.get(docId);
                                const localData = extractList(stateRef.current);
                                const remoteData = extractList(newValue);
                                const merged = customMerger
                                    ? customMerger(localData, remoteData)
                                    : mergeUniversalChronological(localData, remoteData);

                                const currentAny = stateRef.current as any;
                                const finalMerged = (Array.isArray(initialValue) || (currentAny && currentAny.list !== undefined))
                                    ? (currentAny && currentAny.list !== undefined ? { ...currentAny, list: merged } : merged)
                                    : merged;

                                console.log(`[Supabase Sync] Merged realtime update into local uncommitted state for ${docId}`);
                                isRemoteUpdate.current = true;
                                setState(finalMerged);
                                return;
                            } catch (e) {
                                console.warn(`[Supabase Sync] Failed merging realtime update for ${docId}:`, e);
                            }
                            console.log(`[Supabase Sync] Ignoring realtime update for ${docId} due to unsaved local changes waiting to sync.`);
                            return;
                        }

                        console.log(`[Supabase Sync] Realtime update received for ${docId}`);
                        isRemoteUpdate.current = true;
                        setState(newValue);
                        lastSavedState.current = safeStringify(newValue);
                        setIndexedDb(offlineKey, newValue);
                        safeSetLocalStorage(offlineKey, safeStringify(newValue));
                    }
                }
            )
            .subscribe();

        // -----------------------------------------------------------------------------------------
        // REVALIDACIÓN AUTOMÁTICA TRAS INACTIVIDAD (Focus, VisibilityChange y Detección de Inactividad)
        // Si el usuario dejó la pestaña inactiva por más de 10 minutos (o suspendió el equipo),
        // al regresar (click, tecla, foco o visibilidad) se revalida y fusiona silenciosamente con el servidor,
        // reconectando además el canal Realtime si el navegador congeló la conexión WebSocket.
        // -----------------------------------------------------------------------------------------
        let lastUserActiveTimestamp = Date.now();
        let lastFetchTimestamp = 0;

        const markUserActive = () => {
            const now = Date.now();
            const elapsed = now - lastUserActiveTimestamp;
            lastUserActiveTimestamp = now;

            // Si estuvo inactivo más de 5 minutos (300,000 ms), disparar revalidación al retomar la interacción
            if (elapsed > 5 * 60 * 1000) {
                console.log(`[Supabase Sync] User active again after ${(elapsed / 60000).toFixed(1)}m of inactivity. Revalidating ${docId}...`);
                handleTabResume();
            }
        };

        let cameraDeferredTimer: any = null;

        const handleTabResume = () => {
            if (!isMounted.current || document.visibilityState !== 'visible') return;

            // Si la cámara nativa está en progreso o en período de gracia, diferir la revalidación ~3s
            if ((window as any).__klyd_camera_in_progress) {
                console.log(`[Supabase Sync] Camera in progress for ${docId}. Deferring revalidation 3s...`);
                if (cameraDeferredTimer) clearTimeout(cameraDeferredTimer);
                cameraDeferredTimer = setTimeout(() => {
                    if (isMounted.current && !(window as any).__klyd_camera_in_progress) {
                        console.log(`[Supabase Sync] Running deferred revalidation after camera for ${docId}`);
                        fetchInitialData();
                    }
                }, 3000);
                return;
            }

            const now = Date.now();
            // Throttle: evitar múltiples peticiones concurrentes en ráfaga (mínimo 3 segundos entre fetches)
            if (now - lastFetchTimestamp < 3000) return;
            lastFetchTimestamp = now;

            console.log(`[Supabase Sync] Tab resumed/focused for ${docId}. Revalidating state & channel health...`);

            // 1. Revalidar / reconectar canal WebSocket si fue desconectado por el navegador tras suspensión
            try {
                if (channel && (channel.state === 'closed' || channel.state === 'errored')) {
                    console.log(`[Supabase Sync] Realtime channel for ${docId} was closed/errored (${channel.state}). Re-subscribing...`);
                    channel.subscribe();
                }
            } catch (chanErr) {
                console.warn(`[Supabase Sync] Error checking channel status for ${docId}:`, chanErr);
            }

            // 2. Traer los datos remotos más recientes y fusionar cronológicamente
            fetchInitialData();
        };

        const handleManualSyncRefresh = () => {
            console.log(`[Supabase Sync] Manual refresh triggered for ${collectionName}/${docId}`);
            fetchInitialData();
        };

        document.addEventListener('visibilitychange', handleTabResume);
        window.addEventListener('klyd_sync_refresh', handleManualSyncRefresh);
        window.addEventListener('pointerdown', markUserActive, { passive: true });
        window.addEventListener('keydown', markUserActive, { passive: true });

        return () => {
            console.log(`[Supabase Sync] Cleanup ${docId}`);
            isMounted.current = false;
            if (cameraDeferredTimer) clearTimeout(cameraDeferredTimer);
            document.removeEventListener('visibilitychange', handleTabResume);
            window.removeEventListener('klyd_sync_refresh', handleManualSyncRefresh);
            window.removeEventListener('pointerdown', markUserActive);
            window.removeEventListener('keydown', markUserActive);
            supabase.removeChannel(channel);
        };
    }, [collectionName, docId]);

    // 3. Auto-save state changes locally and schedule background server sync
    useEffect(() => {
        if (!hasLoaded) return;

        if (isRemoteUpdate.current) {
            console.log(`[Supabase Sync] Local update for ${docId} skipped (matched remote)`);
            isRemoteUpdate.current = false;
            return;
        }

        if (!isMounted.current) return;

        const currentStateStr = safeStringify(state);
        if (currentStateStr === lastSavedState.current || currentStateStr === "") {
            return;
        }

        const sanitize = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(sanitize);
            } else if (obj !== null && typeof obj === 'object') {
                return Object.fromEntries(
                    Object.entries(obj)
                        .filter(([_, v]) => v !== undefined)
                        .map(([k, v]) => [k, sanitize(v)])
                );
            }
            return obj;
        };

        // Instantly write to offline high-capacity store (IndexedDB) and safe fallback to localStorage
        setIndexedDb(offlineKey, state);
        safeSetLocalStorage(offlineKey, currentStateStr);

        const timer = setTimeout(async () => {
            const nowIso = new Date().toISOString();
            const currentUserName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;

            let rawPayload: any;
            if (Array.isArray(state)) {
                // Preservar timestamps individuales de cada elemento sin envenenar con nowIso
                // los elementos que no hayan sido modificados por el usuario.
                const cleanList = state.map(item => {
                    if (item && typeof item === 'object' && !Array.isArray(item)) {
                        return {
                            ...item,
                            ...(currentUserName && !item.updatedBy ? { updatedBy: currentUserName } : {})
                        };
                    }
                    return item;
                });
                rawPayload = { list: cleanList, updatedAt: nowIso, ...(currentUserName ? { updatedBy: currentUserName } : {}) };
            } else if (state && typeof state === 'object') {
                rawPayload = {
                    ...state,
                    updatedAt: (state as any).updatedAt || nowIso,
                    ...(currentUserName ? { updatedBy: currentUserName } : {})
                };
            } else {
                rawPayload = state;
            }

            const payload = sanitize(rawPayload);

            // If offline, bypass network call immediately and enqueues changes
            if (typeof navigator !== "undefined" && !navigator.onLine) {
                console.log(`[Supabase Sync] Device is offline. Enqueueing sync for ${docId}.`);
                enqueueOfflineSync(collectionName, docId, payload);
                return;
            }

            console.log(`[Supabase Sync] Auto-saving ${docId} to Supabase...`);

            try {
                const finalPayload = await mergeWithRemote(collectionName, docId, payload);
                let savedStr = currentStateStr;
                if (isMounted.current) {
                    // Reflejar localmente los cambios manuales más recientes de otros usuarios
                    const mergedState = Array.isArray(state) ? extractList(finalPayload) : finalPayload;
                    const mergedStr = safeStringify(mergedState);
                    if (mergedStr !== "" && mergedStr !== currentStateStr) {
                        savedStr = mergedStr;
                        isRemoteUpdate.current = true;
                        setState(mergedState);
                        setIndexedDb(offlineKey, mergedState);
                        safeSetLocalStorage(offlineKey, mergedStr);
                    }
                }
                const { error } = await supabase
                    .from('app_data')
                    .upsert({
                        collection_name: collectionName,
                        doc_id: docId,
                        payload: finalPayload,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'collection_name,doc_id' });

                if (error) {
                    console.error(`[Supabase Sync] Error during upsert of ${docId}:`, error);
                    if (isNetworkError(error)) {
                        console.log(`[Supabase Sync] Network issue detected. Enqueueing sync for ${docId}.`);
                        enqueueOfflineSync(collectionName, docId, payload);
                    }
                } else {
                    console.log(`[Supabase Sync] ${docId} saved successfully.`);
                    dequeueOfflineSync(collectionName, docId);
                    if (savedStr !== "") {
                        lastSavedState.current = savedStr;
                    }
                }
            } catch (err: any) {
                console.error(`[Supabase Sync] Network exception saving ${docId}:`, err);
                enqueueOfflineSync(collectionName, docId, payload);
            }
        }, 800); // 800ms debounce

        return () => clearTimeout(timer);
    }, [state, collectionName, docId, hasLoaded]);

    // 4. Manual force sync implementation (useful on important action triggers)
    const forceSync = async (overriddenState?: T) => {
        const stateToSave = overriddenState !== undefined ? overriddenState : stateRef.current;

        const sanitize = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(sanitize);
            } else if (obj !== null && typeof obj === 'object') {
                return Object.fromEntries(
                    Object.entries(obj)
                        .filter(([_, v]) => v !== undefined)
                        .map(([k, v]) => [k, sanitize(v)])
                );
            }
            return obj;
        };

        const nowIso = new Date().toISOString();
        const currentUserName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;

        let rawPayload: any;
        if (Array.isArray(stateToSave)) {
            const stampedList = stateToSave.map(item => {
                if (item && typeof item === 'object' && !Array.isArray(item)) {
                    return {
                        ...item,
                        updatedAt: item.updatedAt || nowIso,
                        ...(currentUserName && !item.updatedBy ? { updatedBy: currentUserName } : {})
                    };
                }
                return item;
            });
            rawPayload = { list: stampedList, updatedAt: nowIso, ...(currentUserName ? { updatedBy: currentUserName } : {}) };
        } else if (stateToSave && typeof stateToSave === 'object') {
            rawPayload = {
                ...stateToSave,
                updatedAt: (stateToSave as any).updatedAt || nowIso,
                ...(currentUserName ? { updatedBy: currentUserName } : {})
            };
        } else {
            rawPayload = stateToSave;
        }

        const payload = sanitize(rawPayload);
        const serialized = safeStringify(stateToSave);

        // Always save locally first (IndexedDB + safe localStorage)
        setIndexedDb(offlineKey, rawPayload || stateToSave);
        safeSetLocalStorage(offlineKey, serialized);

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            console.log(`[Supabase Sync] Offline during forceSync. Queueing ${docId}.`);
            enqueueOfflineSync(collectionName, docId, payload);
            return;
        }

        console.log(`[Supabase Sync] Force syncing ${docId} to Supabase...`);

        try {
            const finalForcePayload = await mergeWithRemote(collectionName, docId, payload);
            const { error } = await supabase
                .from('app_data')
                .upsert({
                    collection_name: collectionName,
                    doc_id: docId,
                    payload: finalForcePayload,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'collection_name,doc_id' });

            if (error) {
                if (isNetworkError(error)) {
                    enqueueOfflineSync(collectionName, docId, payload);
                } else {
                    throw error;
                }
            } else {
                console.log(`[Supabase Sync] ${docId} force-saved successfully.`);
                dequeueOfflineSync(collectionName, docId);
                const finalState = Array.isArray(stateToSave) ? extractList(finalForcePayload) : finalForcePayload;
                const finalStr = safeStringify(finalState);
                lastSavedState.current = finalStr;
                if (finalStr !== serialized && isMounted.current) {
                    isRemoteUpdate.current = true;
                    setState(finalState);
                    setIndexedDb(offlineKey, finalState);
                    safeSetLocalStorage(offlineKey, finalStr);
                }
            }
        } catch (e: any) {
            console.error(`[Supabase Sync] Exception during forceSync for ${docId}:`, e);
            enqueueOfflineSync(collectionName, docId, payload);
        }
    };

    return [state, setState, forceSync, hasLoaded, loadedFromServer] as const;
}
